import {
	tap,
	catchError,
	take,
	map,
	switchMap,
	startWith,
	filter,
	shareReplay,
	share,
} from 'rxjs/operators'
import { BehaviorSubject, EMPTY, firstValueFrom, of, Subject } from 'rxjs'
import { type ClientLoginResponse } from '@lg/shared'
import { Injectable, computed, inject, signal } from '@angular/core'
import { UserRecord } from '@lg/shared/for-apps'
import { type SystemPermissionName } from '@lg/shared'
import { type User } from '@lg/shared'
import { AppTrpcServiceInjectionToken } from './app-trpc.service.js'
import { type LoginRequest } from '@lg/shared'
import { IdbService } from './idb.service.js'
import {
	IdbRefreshTokenProperty,
	IdbUserProperty,
	IdbLoginExpiresAt,
} from '@lg/shared/for-apps'
import { CsrfService } from './csrf.service.js'
import { on$ } from '@lg/shared/for-apps'
import { jsonMapper } from '@lg/shared/for-apps'

export class AuthService {
	private trpc = inject(AppTrpcServiceInjectionToken)
	private idb = inject(IdbService)
	private csrfService = inject(CsrfService)
	private refreshToken = signal<string>('')
	private user = signal<UserRecord | undefined>(undefined)
	private userFullName = computed(() => {
		const user = this.user()
		if (!user) return '(Anonymous)'
		return `${user.firstName} ${user.lastName}\n(${user.userName})`
	})
	private renewTimeout?: number

	private loaded$ = this.csrfService.isLoaded$
	private forceRenewLoginSubject = new Subject<void>()
	readonly loginRenewed$ = on$(this.loaded$, () =>
		this.forceRenewLoginSubject.pipe(
			startWith(undefined),
			switchMap(() => this.renewLogin()),
			filter(Boolean),
			share(),
		),
	)
	readonly loggedIn$ = this.loginRenewed$.pipe(take(1), shareReplay(1))
	heartbeat$ = on$(this.loaded$, () =>
		this.trpc.subscriptionAsObservable((c) => c.auth.heartbeat$, undefined),
	)
	loginConfig$ = on$(this.loaded$, () =>
		this.trpc.queryAsObservable((c) => c.auth.getLoginConfig.query()),
	)
	errorLogger: (message: any) => void = console.error
	isInitialized = signal(false)
	isInitializedPromise = Promise.withResolvers<void>()

	constructor() {
		const userPromise = this.idb.get(IdbUserProperty)
		const refreshTokenPromise = this.idb.get(IdbRefreshTokenProperty)
		Promise.all([userPromise, refreshTokenPromise]).then(
			([user, refreshToken]) => {
				this.user.set(user ? new UserRecord(user) : undefined)
				this.refreshToken.set(refreshToken ?? '')
				this.isInitialized.set(true)
				this.isInitializedPromise.resolve()
			},
		)
	}

	login$(request: LoginRequest) {
		return this.trpc
			.queryAsObservable((c) => c.auth.login.mutate(request))
			.pipe(
				catchError((err) => {
					return of({
						nextStage: request.stage,
						error: err.message,
					} as ClientLoginResponse)
				}),
				switchMap(async (loginResponse: ClientLoginResponse) => {
					if (loginResponse.nextStage == 'successful') {
						await this.setTokens(loginResponse, true)
					}
					if (loginResponse.error) {
						this.errorLogger(loginResponse.error)
					}
					return loginResponse
				}),
				tap((x) => {
					console.log('login response', x)
				}),
			)
	}

	logout$() {
		return this.trpc
			.queryAsObservable((c) => c.auth.logout.mutate())
			.pipe(
				switchMap(async (loginResponse) => {
					await this.setTokens(null, true)
					this.csrfService.refreshCsrfToken()
					return loginResponse
				}),
				catchError((err) => {
					this.errorLogger('Failed to log out')
					return EMPTY
				}),
			)
	}

	requestAuthToken$(request: LoginRequest) {
		return this.trpc
			.queryAsObservable((c) =>
				c.auth.requestAuthToken.mutate({
					userName: request.userName,
					password: request.password,
				}),
			)
			.pipe(
				catchError((err) => {
					this.errorLogger('Failed to request an SMS token')
					return EMPTY
				}),
			)
	}

	private async renewLogin() {
		try {
			const refreshToken = this.refreshToken()
			if (!refreshToken) {
				return undefined
			}
			const loginResponse = await this.trpc.client.auth.renewLogin.mutate({
				refreshToken,
			})
			if (loginResponse.nextStage == 'successful') {
				// console.log('renewed login')
				await this.setTokens(loginResponse, true)
			}
			return loginResponse
		} catch (err) {
			this.errorLogger('Failed to renew login... redirecting to login page')
			if (typeof window != 'undefined') {
				setTimeout(() => {
					const { pathname } = window.location
					window.location.assign(
						'/studio/login?redirect=' + encodeURIComponent(pathname),
					)
				}, 1500)
			}
			return undefined
		}
	}

	async forceRenewLogin() {
		this.forceRenewLoginSubject.next()
		return firstValueFrom(this.loginRenewed$)
	}

	updatePassword(oldPassword: string, newPassword: string) {
		const updatePassword$ = this.trpc
			.queryAsObservable((c) =>
				c.user.updatePassword.mutate({
					oldPassword,
					newPassword,
				}),
			)
			.pipe(
				catchError((err) => {
					return of(false)
				}),
				tap((successful) => {
					if (!successful) {
						this.errorLogger('Failed to update password')
					}
				}),
			)

		return firstValueFrom(updatePassword$)
	}

	isUsernameAvailable(userName: string) {
		const updatePassword$ = this.trpc
			.queryAsObservable((c) =>
				c.user.isUsernameAvailable.query({
					userName,
				}),
			)
			.pipe(
				catchError((err) => {
					this.errorLogger('Failed to get availability')
					return EMPTY
				}),
			)

		return firstValueFrom(updatePassword$)
	}

	async registerUser(user: User, password: string, generatePassword: boolean) {
		return this.trpc.client.user.register.mutate({
			user,
			password,
			generatePassword,
		})
	}

	async startPasswordRecovery(userNameOrEmail: string) {
		return this.trpc.client.user.startPasswordRecovery.mutate({
			userNameOrEmail,
		})
	}

	async finalizePasswordRecovery(
		userName: string,
		recoveryToken: string,
		newPassword: string,
	) {
		return this.trpc.client.user.finalizePasswordRecovery.mutate({
			userName,
			recoveryToken,
			newPassword,
		})
	}

	async setTokens(
		loginResponse: ClientLoginResponse | null,
		storeInIdb: boolean,
	) {
		let doStore = false
		if (loginResponse) {
			this.refreshToken.set(loginResponse.refreshToken ?? '')
			this.user.set(
				jsonMapper.deserializeFromObject(loginResponse.user!, UserRecord),
			)
			doStore = !!this.refreshToken()
		} else {
			this.refreshToken.set('')
			this.user.set(undefined)
			doStore = true
		}

		if (doStore && storeInIdb) {
			if (this.refreshToken()) {
				await this.idb.put(IdbRefreshTokenProperty, this.refreshToken())
				await this.idb.put(IdbUserProperty, this.user())
				await this.idb.put(IdbLoginExpiresAt, loginResponse?.expiresAt ?? 0)

				this.startRenewalTimeout(loginResponse?.expiresAt)
			} else {
				await this.idb.delete(IdbRefreshTokenProperty)
				await this.idb.delete(IdbUserProperty)
				await this.idb.delete(IdbLoginExpiresAt)
			}
		}
	}

	startRenewalTimeout(expiresAt: number | undefined) {
		if (!expiresAt) return
		if (this.renewTimeout) clearTimeout(this.renewTimeout)

		const renewAt = expiresAt - 10_000 + 3000 * Math.random() // add a random delay to avoid simultaneous renewals
		const renewInMs = renewAt - Date.now()

		this.renewTimeout = setTimeout(async () => {
			const expiresAtFromIdb = (await this.idb.get(IdbLoginExpiresAt)) ?? 0
			if (expiresAtFromIdb > expiresAt) {
				// login has already been renewed, start renewal timeout for next renewal
				this.startRenewalTimeout(expiresAtFromIdb)
			} else {
				// login has expired or is about to expire, force renewal
				await this.forceRenewLogin()
			}
		}, renewInMs) as any // renew 10-13s before expiration
	}

	async isLoggedIn() {
		await this.isInitializedPromise.promise
		return !!this.user()?.id
	}

	getUser() {
		return this.user()
	}

	getUserFullName() {
		return this.userFullName()
	}

	hasSystemPermission(permissionName: SystemPermissionName) {
		return this.user()?.hasSystemPermission(permissionName) ?? false
	}
}
